export type GameModeId = "endless" | "timeAttack";
export type SpecialPickupKind =
  | "booster"
  | "magnet"
  | "double"
  | "reverse"
  | "speedLimit"
  | "steeringLimit"
  | "chaos";

const SPECIAL_PICKUP_KINDS: readonly SpecialPickupKind[] = [
  "booster",
  "magnet",
  "double",
  "reverse",
  "speedLimit",
  "steeringLimit",
  "chaos",
];

export interface PickupProfile {
  initialCooldown: number;
  spawnChance: number;
  difficultyBonus: number;
  cooldownMin: number;
  cooldownMax: number;
  weights: Readonly<Record<SpecialPickupKind, number>>;
}

export interface GameMode {
  id: GameModeId;
  label: string;
  shortLabel: string;
  description: string;
  duration: number | null;
  unlimitedNitro: boolean;
  pickups: PickupProfile;
}

export const GAME_MODES: readonly GameMode[] = Object.freeze([
  Object.freeze({
    id: "endless" as const,
    label: "Course infinie",
    shortLabel: "INFINI",
    description: "Survis, prends des risques et bats ton record.",
    duration: null,
    unlimitedNitro: false,
    pickups: Object.freeze({
      initialCooldown: 3.5,
      spawnChance: 0.75,
      difficultyBonus: 0.15,
      cooldownMin: 6,
      cooldownMax: 10,
      weights: Object.freeze({
        booster: 0.34,
        magnet: 0.26,
        double: 0.22,
        reverse: 0.18,
        speedLimit: 0,
        steeringLimit: 0,
        chaos: 0,
      }),
    }),
  }),
  Object.freeze({
    id: "timeAttack" as const,
    label: "Chrono 60 s",
    shortLabel: "CHRONO",
    description: "Nitro illimité, malus sur la route, 60 s pour scorer.",
    duration: 60,
    unlimitedNitro: true,
    pickups: Object.freeze({
      initialCooldown: 0.8,
      spawnChance: 0.92,
      difficultyBonus: 0.08,
      cooldownMin: 2.6,
      cooldownMax: 4.2,
      weights: Object.freeze({
        booster: 0,
        magnet: 0,
        double: 0,
        reverse: 0.28,
        speedLimit: 0.26,
        steeringLimit: 0.24,
        chaos: 0.22,
      }),
    }),
  }),
]);

export function getGameMode(id: GameModeId): GameMode {
  return GAME_MODES.find((mode) => mode.id === id) ?? GAME_MODES[0];
}

export function getTimeRemaining(id: GameModeId, elapsed: number) {
  const duration = getGameMode(id).duration;
  return duration === null ? null : Math.max(0, duration - elapsed);
}

export function getFinalCountdown(
  id: GameModeId,
  elapsed: number,
  countdownFrom = 5,
) {
  const remaining = getTimeRemaining(id, elapsed);
  if (remaining === null || remaining <= 0 || remaining > countdownFrom)
    return null;
  return Math.ceil(remaining);
}

export function isModeComplete(id: GameModeId, elapsed: number) {
  const duration = getGameMode(id).duration;
  return duration !== null && elapsed >= duration;
}

export function getSpecialPickupChance(id: GameModeId, difficulty: number) {
  const profile = getGameMode(id).pickups;
  return Math.min(
    1,
    profile.spawnChance + Math.max(0, difficulty) * profile.difficultyBonus,
  );
}

export function getSpecialPickupCooldown(id: GameModeId, randomValue: number) {
  const profile = getGameMode(id).pickups;
  const ratio = Math.max(0, Math.min(1, randomValue));
  return (
    profile.cooldownMin + (profile.cooldownMax - profile.cooldownMin) * ratio
  );
}

export function pickSpecialPickup(
  id: GameModeId,
  randomValue: number,
): SpecialPickupKind {
  const weights = getGameMode(id).pickups.weights;
  const target = Math.max(0, Math.min(0.999999, randomValue));
  let cumulative = 0;
  for (const kind of SPECIAL_PICKUP_KINDS) {
    cumulative += weights[kind];
    if (target < cumulative) return kind;
  }
  return "reverse";
}
